import {
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  Box,
  Flex,
  Text,
  Tooltip
} from '@chakra-ui/react';
import { faFile, faMinus, faPlus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Badge, Table } from '@morressier/alembic';
import { ISubmissionNotesListItem } from '@publishing-engine/domain/views';
import dateFormat from 'dateformat';
import { useState } from 'react';
import ViewNoteModal from './NotesTab/ViewNoteModal';
import { useSubmissionData } from 'src/contexts/SubmissionDataProvider';
interface Props {
  totalNotesCount: number;
  notes: any;
}

export const NotesAccordion = ({ totalNotesCount, notes }: Props) => {
  const { submissionContext } = useSubmissionData();
  const hasCOCEnabled = submissionContext?.config?.isCOCRecommendationRequired;
  const [selectedNote, setSelectedNote] =
    useState<ISubmissionNotesListItem | null>(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  return (
    <>
      {selectedNote && (
        <ViewNoteModal
          isOpen={isViewModalOpen}
          onClose={() => setIsViewModalOpen(false)}
          data={selectedNote}
        />
      )}
      <Accordion allowToggle defaultIndex={0}>
        <AccordionItem
          border={hasCOCEnabled ? 'none' : '1px solid'}
          borderColor="neutralPrimaryBorder"
          borderRadius={hasCOCEnabled ? undefined : '10px'}
          overflow={hasCOCEnabled ? undefined : 'hidden'}
        >
          {({ isExpanded }) => (
            <>
              <AccordionButton
                padding={1}
                bgColor="surfacePrimaryFill"
                border={hasCOCEnabled ? '1px solid' : 'none'}
                borderColor="neutralPrimaryBorder"
              >
                <Flex
                  width="100%"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  {/* Left title */}
                  <Text
                    textStyle="headingLine"
                    id="notes"
                    ml={!hasCOCEnabled ? 2 : undefined}
                  >
                    Notes ({totalNotesCount})
                  </Text>

                  {/* Right actions */}
                  <Flex gap={3} alignItems="center">
                    <Box ml={2} p={2}>
                      <FontAwesomeIcon
                        icon={isExpanded ? faMinus : faPlus}
                        color="blue"
                      />
                    </Box>
                  </Flex>
                </Flex>
              </AccordionButton>

              <AccordionPanel
                px={4}
                py={3}
                border={hasCOCEnabled ? '1px solid' : 'none'}
                borderTop={hasCOCEnabled ? 'none' : '1px solid'}
                borderColor="neutralPrimaryBorder"
                bgColor="surfaceSecondaryFill"
              >
                {totalNotesCount !== 0 && notes?.length !== 0 ? (
                  <Table>
                    <Table.HeaderRow
                      sx={{
                        '& th': {
                          textTransform: 'none',
                          color: 'black',
                          fontSize: '14px',
                          fontWeight: '500',
                          whiteSpace: 'nowrap'
                        }
                      }}
                    >
                      <Table.HeaderCell flex="1" textAlign="left">
                        Title & Note
                      </Table.HeaderCell>
                      <Table.HeaderCell flex="1" textAlign="left">
                        Users
                      </Table.HeaderCell>
                      <Table.HeaderCell flex="1" textAlign="left">
                        Last updated on
                      </Table.HeaderCell>
                    </Table.HeaderRow>
                    <Table.Body>
                      {notes?.map((note: any) => {
                        return (
                          <Table.Row>
                            <Table.RowCell
                              flex="1"
                              textAlign="left"
                              onClick={() => {
                                setSelectedNote(note);
                                setIsViewModalOpen(true);
                              }}
                              cursor="pointer"
                            >
                              <Tooltip
                                hasArrow
                                borderRadius={4}
                                placement="top"
                                label={'View Note'}
                              >
                                <Box width="100%">
                                  <Flex align="center">
                                    <Text
                                      textStyle="headingLine"
                                      color="#2043DF"
                                      _hover={{
                                        textDecoration: 'underline',
                                        color: '#142480'
                                      }}
                                    >
                                      {note.title?.trim() || 'Untitled'}
                                    </Text>
                                  </Flex>
                                </Box>
                              </Tooltip>
                              {note.title === 'Untitled' && (
                                <Box noOfLines={2} cursor="pointer">
                                  <Text textStyle="textBody">
                                    {note?.content}
                                  </Text>
                                </Box>
                              )}
                              <Flex alignItems="center" gap={2} mt={1}>
                                {note?.attachments &&
                                  note?.attachments?.length > 0 && (
                                    <Flex alignItems="center" gap={1}>
                                      <FontAwesomeIcon
                                        icon={faFile}
                                        size="sm"
                                        color="gray.700"
                                      />
                                      <Text
                                        textStyle="textBody"
                                        color="gray.700"
                                        noOfLines={1}
                                      >
                                        {note.attachments.length}{' '}
                                        {note.attachments.length > 1
                                          ? 'Files'
                                          : 'File'}
                                      </Text>
                                    </Flex>
                                  )}

                                {note?.category && (
                                  <Badge variant="neutral" size="context">
                                    {note.category}
                                  </Badge>
                                )}
                              </Flex>
                            </Table.RowCell>
                            <Table.RowCell flex="1" textAlign="left">
                              <Text textStyle="headingLine">
                                {`${note?.addedBy?.firstName ?? ''} ${
                                  note?.addedBy?.lastName ?? ''
                                }`.trim()}
                              </Text>
                              {note?.mentions && note?.mentions?.length > 0 && (
                                <Text>
                                  @{note?.mentions[0]?.name}
                                  {note.mentions.length > 1 &&
                                    `, +${note.mentions.length - 1}`}
                                </Text>
                              )}
                            </Table.RowCell>

                            <Table.RowCell flex="1" textAlign="left">
                              <Text textStyle="headingItem">
                                {dateFormat(note?.modified_at, 'mmm d, yyyy')}
                              </Text>
                            </Table.RowCell>
                          </Table.Row>
                        );
                      })}
                    </Table.Body>
                  </Table>
                ) : (
                  <Flex
                    justifyContent={!hasCOCEnabled ? 'flex-start' : 'center'}
                    alignItems="center"
                    padding={!hasCOCEnabled ? 2 : undefined}
                    minH={!hasCOCEnabled ? '100px' : undefined}
                  >
                    <Text textStyle="textContext">No notes added yet</Text>
                  </Flex>
                )}
              </AccordionPanel>
            </>
          )}
        </AccordionItem>
      </Accordion>
    </>
  );
};
